import PropTypes from 'prop-types';
import React from 'react';
import { join } from 'path';
import EditorComponent from '../editor/editorComponent';
import FeedbackContextStrip from './feedbackContextStrip';
import FeedbackFormShell from './feedbackFormShell';
import FeedbackScreenshot from './feedbackScreenshot';
import entityComposition from '../common/classes/entityComposition';
import { PROBLEM_CATEGORY_ID } from './feedbackConstants';
import { canSubmitFeedback, readFeedbackValues } from './feedbackPayload';
import { displayContextShape, feedbackCategoryShape } from './feedbackPropTypes';

// Must be defined outside the component to avoid re-rendering loops. The feedback draft never
// waits on a server round trip, so there is no pending-operation state to track.
const noOperationState = () => {};

/**
 * The fields of every feedback category come from its FormType `formatInput`, rendered by the same
 * `EditorComponent` the rest of the app uses. The draft lives in the shared `formModal` state, so
 * field changes run through `formModalActions.change` exactly as they do inside `FormModal`.
 */
const FeedbackForm = ({
  category,
  context,
  onBack,
  onSubmit,
  isSubmitting,
  hasSubmitError,
  formModal: {
    active: _active = null
  } = {},
  formModalActions: {
    change
  },
  screenshotsAllowed: _screenshotsAllowed = true,
  screenshotBlob: _screenshotBlob = null,
  isCapturingScreenshot: _isCapturingScreenshot = false,
  hasScreenshotError: _hasScreenshotError = false,
  onRemoveScreenshot,
  onRetakeScreenshot
}) => {
  if (!_active || !_active.format) {
    return null;
  }
  const {
    id,
    format,
    content
  } = _active;
  const values = readFeedbackValues(content);
  const isProblem = category.id === PROBLEM_CATEGORY_ID;
  const handleChange = ({
    path,
    value,
    changeModifiers
  }) => {
    change(id, join('content', path), value, changeModifiers);
  };
  return /*#__PURE__*/React.createElement(FeedbackFormShell, {
    category: category,
    onBack: onBack,
    onSubmit: () => onSubmit(values),
    isSubmitDisabled: !canSubmitFeedback({
      categoryId: category.id,
      values,
      isSubmitting
    }),
    isSubmitting: isSubmitting,
    hasSubmitError: hasSubmitError
  }, isProblem ? /*#__PURE__*/React.createElement(FeedbackContextStrip, {
    context: context
  }) : null, isProblem && _screenshotsAllowed ? /*#__PURE__*/React.createElement(FeedbackScreenshot, {
    previewUrl: _screenshotBlob,
    isCapturing: _isCapturingScreenshot,
    hasError: _hasScreenshotError,
    onRemove: onRemoveScreenshot,
    onRetake: onRetakeScreenshot
  }) : null, /*#__PURE__*/React.createElement("div", {
    className: "t-feedback-form__editor"
  }, /*#__PURE__*/React.createElement(EditorComponent, {
    reduxEntity: "formModal",
    path: "/",
    format: format,
    onChange: handleChange,
    operationComplete: noOperationState,
    operationPending: noOperationState
  })));
};
FeedbackForm.propTypes = {
  category: feedbackCategoryShape.isRequired,
  context: displayContextShape.isRequired,
  onBack: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  hasSubmitError: PropTypes.bool.isRequired,
  formModal: PropTypes.shape({
    active: PropTypes.shape({})
  }),
  formModalActions: PropTypes.shape({
    change: PropTypes.func.isRequired
  }).isRequired,
  screenshotsAllowed: PropTypes.bool,
  screenshotBlob: PropTypes.instanceOf(Blob),
  isCapturingScreenshot: PropTypes.bool,
  hasScreenshotError: PropTypes.bool,
  onRemoveScreenshot: PropTypes.func.isRequired,
  onRetakeScreenshot: PropTypes.func.isRequired
};
export { FeedbackForm as TestFeedbackForm };
export default entityComposition(FeedbackForm, ['formModal']);